import { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/axiosConfig';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Loader2 } from 'lucide-react';
import { mondayOfWeekYmd } from '../../utils/blockCalendarWeekUtils';

function todayYmd() {
  return new Date().toISOString().slice(0, 10);
}

function defaultStrategyForBlock(block) {
  return block?.block_kind === 'target_race' ? 'trim_past' : 'reanchor';
}

export default function RealignPlanModal({ open, onOpenChange, block, onRealigned }) {
  const { t } = useLanguage();
  const [startMode, setStartMode] = useState('current_week');
  const [customDate, setCustomDate] = useState('');
  const [strategy, setStrategy] = useState('reanchor');
  const [regenerateSessions, setRegenerateSessions] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !block) return;
    setStrategy(defaultStrategyForBlock(block));
    setStartMode('current_week');
    setCustomDate('');
    setRegenerateSessions(true);
  }, [open, block?.id, block?.block_kind]);

  const currentWeekMonday = useMemo(() => mondayOfWeekYmd(todayYmd()), []);
  const anchorPreview = startMode === 'current_week' ? currentWeekMonday : customDate || '—';

  const handleSubmit = async () => {
    if (!block?.id) return;
    if (startMode === 'custom' && !customDate) return;
    setSaving(true);
    try {
      const res = await api.post(`/api/training/periodization/blocks/${block.id}/realign`, {
        anchor_date: startMode === 'current_week' ? currentWeekMonday : customDate,
        strategy,
        regenerate_sessions: regenerateSessions,
      });
      onRealigned?.(res.data.tree);
      onOpenChange(false);
    } catch (e) {
      window.alert(e.response?.data?.error || t('periodization.realignPlan.error'));
    } finally {
      setSaving(false);
    }
  };

  if (!block) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-[min(520px,92vw)]'>
        <DialogHeader>
          <DialogTitle>{t('periodization.realignPlan.title')}</DialogTitle>
          <DialogDescription>{t('periodization.realignPlan.description')}</DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-2'>
          <div className='rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground'>
            {t('periodization.realignPlan.currentRange', {
              from: block.start_date,
              to: block.target_race_date || block.end_date,
            })}
          </div>

          <fieldset className='space-y-2'>
            <legend className='text-sm font-medium'>{t('periodization.realignPlan.startFrom')}</legend>
            <label className='flex cursor-pointer items-center gap-2 text-sm'>
              <input
                type='radio'
                name='startMode'
                checked={startMode === 'current_week'}
                onChange={() => setStartMode('current_week')}
              />
              {t('periodization.realignPlan.currentWeek', { date: currentWeekMonday })}
            </label>
            <label className='flex cursor-pointer items-center gap-2 text-sm'>
              <input
                type='radio'
                name='startMode'
                checked={startMode === 'custom'}
                onChange={() => setStartMode('custom')}
              />
              {t('periodization.realignPlan.customDate')}
            </label>
            {startMode === 'custom' ? (
              <Input type='date' value={customDate} onChange={(e) => setCustomDate(e.target.value)} />
            ) : null}
          </fieldset>

          <fieldset className='space-y-2'>
            <legend className='text-sm font-medium'>{t('periodization.realignPlan.strategy')}</legend>
            <label className='flex cursor-pointer items-start gap-2 text-sm'>
              <input
                type='radio'
                name='strategy'
                className='mt-1'
                checked={strategy === 'reanchor'}
                onChange={() => setStrategy('reanchor')}
              />
              <span>
                <span className='font-medium'>{t('periodization.realignPlan.reanchorTitle')}</span>
                <span className='block text-xs text-muted-foreground'>
                  {t('periodization.realignPlan.reanchorHint')}
                </span>
              </span>
            </label>
            <label className='flex cursor-pointer items-start gap-2 text-sm'>
              <input
                type='radio'
                name='strategy'
                className='mt-1'
                checked={strategy === 'trim_past'}
                onChange={() => setStrategy('trim_past')}
              />
              <span>
                <span className='font-medium'>{t('periodization.realignPlan.trimTitle')}</span>
                <span className='block text-xs text-muted-foreground'>
                  {t('periodization.realignPlan.trimHint')}
                </span>
              </span>
            </label>
          </fieldset>

          <div className='flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2'>
            <div>
              <Label htmlFor='realign-regen' className='text-sm'>
                {t('periodization.realignPlan.regenerateSessions')}
              </Label>
              <p className='text-xs text-muted-foreground'>{t('periodization.realignPlan.regenerateHint')}</p>
            </div>
            <Switch id='realign-regen' checked={regenerateSessions} onCheckedChange={setRegenerateSessions} />
          </div>

          <p className='text-xs text-muted-foreground'>
            {t('periodization.realignPlan.preview', { date: anchorPreview })}
          </p>
        </div>

        <DialogFooter>
          <Button type='button' variant='outline' onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button type='button' onClick={handleSubmit} disabled={saving || (startMode === 'custom' && !customDate)}>
            {saving ? <Loader2 className='h-4 w-4 animate-spin' /> : t('periodization.realignPlan.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
